<template>
  <div class="container-fluid payout-page">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header">
          <PageHeader :page-title="'Payouts'">
            <template v-slot:buttons>
              <b-button
                class="button title-container__controls-item"
                variant="danger"
                @click="removeInBulk"
              >
                <span class="material-icons icon-image-preview">
                  delete
                </span>
                Delete All
              </b-button>
              <b-button
                class="button title-container__controls-item"
                variant="secondary"
                @click="exportPayouts"
              >
                <span class="material-icons icon-image-preview">
                  download
                </span>
                Export XLS
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowBulkCreation ? 'secondary' : 'info'"
                @click="toggleShowBulkCreation"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Bulk Creation
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                v-if="$can(permissions.CreateFinancesPayouts)"
                type="button"
                class="button"
                @click.prevent="showCreateModal"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="filters"
            :isLoading="isDataPageLoading"
            :data-changes="true"
            @changes="checkChanges"
            @submit="setFilters"
          />

          <FinancePayoutsBulkCreation
            v-show="isShowBulkCreation"
            :templates="getPayoutsTemplateTypes"
            @updatePayouts="updatePayouts"
          />

          <Preloader v-if="isDataPageLoading" />

          <div v-if="getPayoutsTableData.length">
            <TableForPage
              ref="table"
              :tableData="getPayoutsTableData"
              :fields="getTableField"
              :perPage="getPayouts.per_page"
              :currentPage="getPayouts.current_page"
              :sortBy="sortField"
              :totalRows="getPayouts.total"
              :slots="table.slots"
              :tableRowModalFields="tableRowModal.fields"
              :actions="table.actions"
              isServerRender
              noLocalSorting
              showTotalTable
              @remove="removePayout"
              @update="updatePayouts"
            >
              <template v-slot:own_bank_details="slotProps">
                <LabelBankCard :card_details="slotProps.item.own_bank_details.summary" />
              </template>
              <template v-slot:payee_bank_details="slotProps">
                <LabelBankCard :card_details="slotProps.item.payee_bank_details ? slotProps.item.payee_bank_details.summary : ''" />
              </template>
              <template v-slot:payout_status="slotProps">
                <LabelTableStatus :status="slotProps.item.payout_status" />
              </template>
              <template v-slot:amount="slotProps">
                {{ slotProps.item.amount }} {{ slotProps.item.currency.code }}
              </template>
              <template v-slot:files="slotProps">
                <PayoutsLinks
                  :templates="getPayoutsTemplateTypes"
                  :item="slotProps.item"
                  @showEditPayoutsModal="showEditModal($event)"
                  @markAsPaid="markPaid($event)"
                />
              </template>
            </TableForPage>
            <div>
              <div class="mt-3">
                <b-card-group deck class="mb-3">
                  <b-card border-variant="dark" class="text-center">
                    <b-card-text>
                      <h3>Original totals</h3>
                      <span v-for="(amount, currencyCode) in totalAmount.original" :key="currencyCode">
                            {{ amount }}
                            <b-badge variant="light"> {{currencyCode}} </b-badge>
                        </span>
                    </b-card-text>
                  </b-card>

                  <b-card border-variant="dark" align="center">
                    <b-card-text>
                      <h3>Equivalent totals</h3>
                      <span v-for="(amount, currencyCode) in totalAmount.equivalent" :key="currencyCode">
                            {{ amount }}
                            <b-badge variant="light"> {{currencyCode}} </b-badge>
                        </span>
                    </b-card-text>
                  </b-card>

                </b-card-group>
              </div>
            </div>
          </div>
          <div class="table__empty" v-else-if="getPayouts.data">
            No payouts for this period
          </div>
        </div>
      </div>
    </div>

    <FinancePayoutsModal
      v-model="createModal.show"
      mode="create"
      :data="createModal.form"
      :title="createModal.title"
      :submitButtonText="createModal.submitButtonText"
      @submit="createFinancePayouts"
    />
    <FinancePayoutsModal
      v-model="editModal.show"
      mode="edit"
      :data="editModal.form"
      :title="editModal.title"
      :submitButtonText="editModal.submitButtonText"
      @submit="editFinancePayouts"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import Moment from 'moment'
import { mapGetters } from 'vuex'
import { permissions } from '@mocks/permissions'
import Preloader from '@components/Preloader'
import TableForPage from '@tables/TableForPage'
import FinanceMixin from '../mixins/FinanceMixin'
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import FinancePayoutsModal from '@components/modals/FinancePayoutsModal/FinancePayoutsModal'
import LabelBankCard from '@components/tables/elements/LabelBankCard'
import LabelTableStatus from '@components/tables/elements/LabelTableStatus'
import PayoutsLinks from '../components/PayoutsLinks'
import FinancePayoutsBulkCreation from '../components/FinancePayoutsBulkCreation'

export default {
  components: {
    Preloader,
    PageHeader,
    Filters,
    TableForPage,
    FinancePayoutsModal,
    LabelBankCard,
    LabelTableStatus,
    PayoutsLinks,
    FinancePayoutsBulkCreation
  },
  mixins: [FinanceMixin],
  data () {
    return {
      permissions,
      modalTitle: 'payouts',
      isDataPageLoading: false,
      isShowBulkCreation: false,
      table: {
        slots: {
          own_bank_details: {
            name: 'own_bank_details'
          },
          payee_bank_details: {
            name: 'payee_bank_details'
          },
          payout_status: {
            name: 'payout_status'
          },
          amount: {
            name: 'amount'
          },
          files: {
            name: 'files'
          }
        },
        fields: [
          {
            key: 'payee_name',
            label: 'Payee name',
            sortable: true,
            class: 'column-payee-name'
          },
          {
            key: 'own_bank_details',
            label: 'Own bank details',
            class: 'column-type text-center'
          },
          {
            key: 'payee_bank_details',
            label: 'Payee bank details',
            class: 'column-type text-center'
          },
          {
            key: 'payout_status',
            label: 'Status',
            class: 'column-icon text-center'
          },
          {
            key: 'issue_date',
            label: 'Issue date',
            sortable: true,
            class: 'column-date'
          },
          {
            key: 'due_date',
            label: 'Due date',
            sortable: true,
            class: 'column-date'
          },
          {
            key: 'paid_date',
            label: 'Paid date',
            sortable: true,
            class: 'column-date'
          },
          {
            key: 'amount',
            label: 'Amount',
            class: 'column-amount'
          },
          {
            key: 'files',
            label: 'Files',
            class: 'column-files column-icon text-center'
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'column-more'
          }
        ],
        actions: [
          { label: 'Delete', event: 'remove', show: this.$can(permissions.DeleteFinancesPayouts) }
        ],
        params: {
          filters: null,
          sorting: null,
          currentPage: '?page=1'
        }
      },
      filters: {
        'payout_number': {
          type: 'input',
          name: 'payout_number',
          label: 'Payout number',
          placeholder: 'Payout number'
        },
        'contractor': {
          type: 'select',
          name: 'contractor_id',
          label: 'Contractor',
          selectLabel: 'label',
          valueField: 'id',
          allowEmpty: true,
          options: [],
          value: []
        },
        'partner': {
          type: 'select',
          name: 'partner_id',
          label: 'Partner',
          selectLabel: 'company_name',
          valueField: 'id',
          allowEmpty: true,
          options: [],
          value: []
        },
        'payout_status': {
          type: 'select',
          name: 'payout_status_id',
          label: 'Payout status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: []
        },
        'own_bank_details': {
          type: 'multiselect',
          name: 'own_bank_details_id',
          nameItem: 'own_bank_details_id',
          label: 'Own bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: []
        },
        'payee_bank_details': {
          type: 'multiselect',
          name: 'payee_bank_details_id',
          nameItem: 'payee_bank_details_id',
          label: 'Payee bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: []
        },
        'date_from': {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').toISOString()
        },
        'date_to': {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').toISOString()
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      getPayouts: 'finances/getPayouts',
      getAllPartners: 'partners/getAllPartners',
      getContractorBankDetails: 'finances/getContractorBankDetails',
      getPartnerBankDetails: 'finances/getPartnerBankDetails',
      getOwnBankDetails: 'finances/getOwnBankDetails',
      getPayoutsStatuses: 'finances/getPayoutsStatuses',
      getPayoutsTemplateTypes: 'finances/getPayoutsTemplateTypes'
    }),
    getTableField () {
      return JSON.parse(JSON.stringify(this.table.fields))
    },
    getPayoutsTableData () {
      if (!this.getPayouts.data) return []

      return this.getPayouts.data.map(item => {
        return {
          ...item,
          due_date: this._toLocalTime(item.due_date),
          paid_date: this._toLocalTime(item.paid_date),
          issue_date: this._toLocalTime(item.issue_date)
        }
      })
    },
    totalAmount () {
      return this.getPayouts.totalAmount || 0
    },
    tableParams () {
      let params = this.table.params.currentPage

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }
      return params
    },
    sortField () {
      if (this.table.params.sorting) {
        let arr = this.table.params.sorting.match(/sort\[([a-zA-Z_]*)\]/)
        return arr[1] ?? 'payee_name'
      } else {
        return 'payee_name'
      }
    }
  },
  watch: {
    'filters.contractor.value': {
      deep: true,
      handler (contractor) {
        this.filters.payee_bank_details.options = []
        if (contractor && Object.keys(contractor).length) {
          this.filters.partner.disabled = true
          this.filters.payee_bank_details.label = 'Contractor bank details'
          this.filters.payee_bank_details.options = this.getContractorBankDetails.filter(item => {
            return item.contractor_id === contractor.id
          })
        } else {
          this.filters.partner.disabled = false
        }
        this.filters.payee_bank_details.value = null
      }
    },
    'filters.partner.value': {
      deep: true,
      handler (partner) {
        this.filters.payee_bank_details.options = []
        if (partner && Object.keys(partner).length) {
          this.filters.contractor.disabled = true
          this.filters.payee_bank_details.label = 'Partner bank details'
          this.filters.payee_bank_details.options = this.getPartnerBankDetails.filter(item => {
            return item.partner_id === partner.id
          })
        } else {
          this.filters.contractor.disabled = false
        }
        this.filters.payee_bank_details.value = null
      }
    }
  },
  created () {
    return Promise.all([
      this.$api.partners.getAllPartners(),
      this.$api.finances.getPayoutsStatuses(),
      this.$api.finances.getPayoutsTemplateTypes(),
      this.$api.finances.getPayoutsTypes()
    ])
      .then(values => {
        this.setAllOptionsFilter()
      })
  },
  methods: {
    fetchPayouts () {
      this._toggleDataPageLoading(() => this.$api.finances.getPayouts(this.tableParams + '&pagination=1'))
    },
    updatePayouts (data) {
      if (!data) return this.fetchPayouts()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchPayouts()
    },
    setAllOptionsFilter () {
      this.filters.contractor.options = this.getAllUsers
      this.filters.partner.options = this.getAllPartners
      this.filters.own_bank_details.options = this.getOwnBankDetails
      this.filters.payout_status.options = this.getPayoutsStatuses
    },
    async setFilters (data) {
      const filterData = cloneDeep(data)
      filterData.date_from.value = this._toLocalTime(filterData.date_from.value, 'YYYY-MM-DD')
      filterData.date_to.value = this._toLocalTime(filterData.date_to.value, 'YYYY-MM-DD')
      await this.setParamsFilters(filterData)
      await this.setParamsCurrentPage()
      this.fetchPayouts()
    },
    showEditModal (params) {
      if (this.$can(this.permissions.UpdateAllFinancesPayouts)) {
        this.editModal.show = true
        this.editModal.form = cloneDeep(params)
      }
    },
    async createFinancePayouts (data) {
      this._toggleDataPageLoading(async () => {
        await this.$api.finances.createPayouts(data)
        this.hideCreateModal()
        this.updatePayouts()
      })
    },
    editFinancePayouts (data) {
      this._toggleDataPageLoading(async () => {
        await this.$api.finances.updatePayouts(data)
        this.hideEditModal()
        this.updatePayouts()
      })
    },
    async removePayout (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the payout?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        await this.$api.finances.removePayouts(item.id)
        this.updatePayouts()
      })
    },
    async markPaid (payout) {
      this._toggleDataPageLoading(async () => {
        const marked = await this.$api.finances.markPayoutPaid(payout.id)
        if (marked.data) {
          this.$snotify.success(
            'Payout successfully marked as paid',
            'Success',
            this.$store.state.snotify
          )
          this.updatePayouts()
        }
      })
    },
    async removeInBulk () {
      const payouts = await this._toggleDataPageLoading(async () => {
        return this.$api.finances.getPayouts(this.tableParams, false)
      })

      const removeItemsPreviews = payouts.data.map(item => {
        return item.payee_name + ' (' + Moment(item.issue_date).format('MMM DD YYYY') + ')'
      })
      const payoutIds = payouts.data.map(item => item.id)
      const confirm = await this.$root.$confirm.open(
        'Remove all payout from the list',
        'Are you sure you want to delete ' + removeItemsPreviews.length + ' payouts?<br>' +
        removeItemsPreviews.join('<br>')
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const response = await this.$api.finances.removePayoutsInBulk({ 'payout_ids': payoutIds })
        if (response.status === 200) {
          this.$snotify.success(
            response.data.message,
            'Success',
            this.$store.state.snotify
          )
        }
        this.updatePayouts()
      })
    },
    checkChanges (fields) {
      this.filters = fields
      this.$forceUpdate()
    },
    toggleShowBulkCreation () {
      this.isShowBulkCreation = !this.isShowBulkCreation
    },
    exportPayouts () {
      this._toggleDataPageLoading(async () => {
        return this.axios.post('/api/finances/payouts/export', this.tableParams, {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
          .then((response) => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]))
            const fileLink = document.createElement('a')
            fileLink.href = fileURL
            const contentDisposition = response.headers['content-disposition']
            const fileNamePosition = contentDisposition.indexOf('filename=') + 9
            const fileName = contentDisposition.substr(fileNamePosition)

            fileLink.setAttribute('download', fileName)
            document.body.appendChild(fileLink)
            fileLink.click()
          })
      })
    }
  }
}
</script>

<style lang="scss">
  .payout-page .table__theme-default .column-payee-name {
    min-width: 120px;
    width: 180px;
  }
  .payout-page .table__theme-default .column-date {
    min-width: 85px;
  }
  .payout-page .table__theme-default .column-amount {
    min-width: 90px
  }
  .filter {
    margin-bottom: 10px;
  }
</style>
